import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { Role } from '../models/Role';
import { Department } from '../models/Department';
import { RoleAssignment } from '../models/RoleAssignment';
import { EffectiveUserRoleAssignment, UserRoleAssignment } from '../models/UserRoleAssignment';
import { User } from '../models/User';
import { RolePermissions, RolePermissionsResponse } from '../models/Permission';
import { AdminPortalAPIHttpClient } from '../../../infrastructure/http/v2/AdminPortalAPIHttpClient';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  constructor(
    private adminPortalHttpClient: AdminPortalAPIHttpClient,
    private snackbar: MatSnackBar
  ) { }

  async getRoles(): Promise<Role[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get('/roles'));
      return response ?? [];
    } catch (e) {
      return Promise.reject(`Error while retrieving roles: ${e}`);
    }
  }

  async createRole(name: string): Promise<Role> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.post('/roles', { name }));
      this.displaySuccessSnackbar();
      return response;
    } catch (e) {
      this.displayErrorSnackbar(`${e}`);
      return Promise.reject(`Error while creating role: ${e}`);
    }
  }

  async updateRole(id: string, name: string): Promise<Role> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/roles/${id}`, { name }));
      this.displaySuccessSnackbar();
      return response;
    } catch (e) {
      this.displayErrorSnackbar(`${e}`);
      return Promise.reject(`Error while updating role: ${e}`);
    }
  }

  async deleteRole(id: string): Promise<void> {
    try {
      await firstValueFrom(this.adminPortalHttpClient.delete(`/roles/${id}`));
      this.displaySuccessSnackbar();
    } catch (e) {
      this.displayErrorSnackbar(`${e}`);
      return Promise.reject(`Error while deleting role: ${e}`);
    }
  }

  async getCloudDepartments(): Promise<Department[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get('/cloud-departments'));
      return response ?? [];
    } catch (e) {
      return Promise.reject(`Error while retrieving cloud departments: ${e}`);
    }
  }

  async getRoleAssignments(): Promise<RoleAssignment[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get('/department-role-assignments'));
      return response ?? [];
    } catch (e) {
      return Promise.reject(`Error while retrieving role assignments: ${e}`);
    }
  }

  async saveRoleAssignments(assignments: RoleAssignment[]): Promise<RoleAssignment[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.post('/department-role-assignments', assignments));
      this.displaySuccessSnackbar();
      return response ?? assignments;
    } catch (e) {
      this.displayErrorSnackbar(`${e}`);
      return Promise.reject(`Error while saving role assignments: ${e}`);
    }
  }

  async getCloudUsers(): Promise<User[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get('/cloud-users'));
      return response ?? [];
    } catch (e) {
      return Promise.reject(`Error while retrieving cloud users: ${e}`);
    }
  }

  async getUserRoleAssignments(): Promise<EffectiveUserRoleAssignment[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get('/user-role-assignments'));
      return response ?? [];
    } catch (e) {
      return Promise.reject(`Error while retrieving user role assignments: ${e}`);
    }
  }

  async saveUserRoleAssignments(assignments: UserRoleAssignment[]): Promise<UserRoleAssignment[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.post('/user-role-assignments', assignments));
      this.displaySuccessSnackbar();
      return response ?? assignments;
    } catch (e) {
      this.displayErrorSnackbar(`${e}`);
      return Promise.reject(`Error while saving user role assignments: ${e}`);
    }
  }

  // The role with its module ids and "{moduleId}-{operationId}" keys, for the role details page.
  async getRolePermissions(roleId: string): Promise<RolePermissionsResponse> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/roles/${roleId}/permissions`));
      return {
        roleId: response?.roleId ?? roleId,
        roleName: response?.roleName ?? '',
        superAdmin: response?.superAdmin === true,
        moduleIds: response?.moduleIds ?? [],
        permissions: response?.permissions ?? []
      };
    } catch (e) {
      return Promise.reject(`Error while retrieving permissions for role ${roleId}: ${e}`);
    }
  }

  // Replaces one role's modules and operation keys in a single call; the API drops operations for modules not granted.
  async saveRolePermissions(roleId: string, permissions: RolePermissions): Promise<RolePermissions> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/roles/${roleId}/permissions`, permissions));
      this.displaySuccessSnackbar();
      return response ?? permissions;
    } catch (e) {
      this.displayErrorSnackbar(`${e}`);
      return Promise.reject(`Error while saving permissions for role ${roleId}: ${e}`);
    }
  }

  displaySuccessSnackbar() {
    this.snackbar.open('Save Successful', 'OK', { duration: 4000 });
  }

  displayErrorSnackbar(errorMessage: string) {
    this.snackbar.open(`Error Saving Permissions: ${errorMessage}`, 'OK', { duration: 4000 });
  }
}
